/**
 * Generate a table of contents from HTML content by extracting h2 and h3
 * headings. Injects `id` attributes on headings that lack them.
 *
 * @param {string} html - Raw HTML string
 * @returns {{ toc: Array<{id: string, text: string, level: number}>, html: string }}
 */
export default function generateTableOfContents(html) {
  if (!html) return { toc: [], html: "" };

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const headings = doc.querySelectorAll("h2, h3");

  const usedIds = new Set();

  const toc = Array.from(headings).map((heading) => {
    const text = heading.textContent.trim();
    const level = heading.tagName === "H2" ? 2 : 3;

    const id = heading.id || slugify(text, usedIds);
    heading.setAttribute("id", id);
    usedIds.add(id);

    return { id, text, level };
  });

  return { toc, html: doc.body.innerHTML };
}

function slugify(text, usedIds) {
  const base = text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/[\s-]+/g, "-")
    .replace(/^-+|-+$/g, "");

  const slug = base || "section";

  if (!usedIds.has(slug)) return slug;

  const unique = Array.from({ length: 100 }, (_, i) => `${slug}-${i + 2}`).find(
    (candidate) => !usedIds.has(candidate),
  );

  return unique ?? `${slug}-${Date.now()}`;
}
